import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Search, Calendar, User } from "lucide-react";
import strings from "../../../utils/Strings";
import JobSearchClient, { JobSearchHistory } from "../data/api/JobSearchClient";
import NewJobSearchModal from "../components/NewJobSearchModal";
import Loader from "../../../shared/components/Loader";
import "../components/styles/JobSearchStyles.css";

const JobSearchListPage: React.FC = () => {
  const navigate = useNavigate();
  const [searches, setSearches] = useState<JobSearchHistory[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const fetchSearches = async () => {
    setLoading(true);
    try {
      const data = await JobSearchClient.listJobSearches();
      // Ensure searches are sorted latest first based on search_date if not already done by API
      const sorted = (data.records || []).sort((a, b) => {
        // Since format is DD-MM-YYYY-HH:MM:SS, it's easier to compare if we reverse it or just assume backend sorted it.
        // We'll leave it as is if backend already sorts it.
        return 0;
      });
      setSearches(sorted);
    } catch (err: any) {
      setError(err.message || "Failed to load talent searches");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSearches();
  }, []);

  const handleNewSearchSuccess = (searchId?: string) => {
    setIsModalOpen(false);
    if (searchId) {
      navigate(`/dashboard/job-search/${searchId}`);
    } else {
      fetchSearches();
    }
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return "";
    // If it's already in DD-MM-YYYY-HH:MM:SS format, replace the 3rd dash with a space
    const parts = dateStr.split('-');
    if (parts.length >= 5) {
      return `${parts[0]}-${parts[1]}-${parts[2]} ${parts[3]}:${parts[4]}${parts[5] ? ':' + parts[5] : ''}`;
    }
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      return d.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return dateStr;
    }
  };

  const filteredSearches = searches.filter(search =>
    search.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <motion.div
      className="learner-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.25 }}
    >
      <Loader isLoading={loading} message="Loading talent searches..." />

      <div className="lp-hero">
        <div className="lp-hero-left">
          <h1 style={{ margin: '0 0 8px 0', fontSize: '24px', color: '#0f172a' }}>{strings.jobSearch.pageTitle}</h1>
          <span className="lp-hero-chip">{strings.jobSearch.subtitle}</span>
        </div>
        <div className="lp-hero-right">
          <button className="btn-primary" onClick={() => setIsModalOpen(true)}>
            <Search size={16} />
            {strings.jobSearch.newSearch}
          </button>
        </div>
      </div>

      <div className="lp-table-panel" style={{ marginTop: "16px" }}>

        {/* Toolbar */}
        <div className="lp-table-panel-head" style={{ padding: '16px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #e2e8f0', background: '#f8fafc', flexWrap: 'wrap', gap: '16px', marginBottom: '16px', borderTopLeftRadius: '14px', borderTopRightRadius: '14px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '8px 16px', gap: '12px', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
              <div style={{ width: 32, height: 32, borderRadius: '8px', background: 'rgba(5, 150, 105, 0.1)', color: 'var(--primary)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Search size={16} strokeWidth={2.5} />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <div style={{ fontSize: '1rem', color: '#64748b', fontWeight: 600, textTransform: 'none', letterSpacing: '0.5px' }}>Total Searches:</div>
                <div style={{ fontSize: '1.25rem', fontWeight: 800, color: '#0f172a', lineHeight: 1, letterSpacing: '-0.5px' }}>{searches.length}</div>
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            {/* Search */}
            <div className="lp-search" style={{ height: 40, width: 280, background: '#fff', boxShadow: '0 1px 2px rgba(0,0,0,0.05)', position: 'relative' }}>
              <Search size={16} color="#94a3b8" />
              <input
                type="text"
                placeholder="Search by title..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                style={{ paddingRight: searchTerm ? '30px' : '10px', height: '100%', border: 'none', background: 'transparent' }}
              />
              {searchTerm && (
                <button
                  style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', border: 'none', background: '#f1f5f9', cursor: 'pointer', color: '#64748b', width: '20px', height: '20px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}
                  onClick={() => setSearchTerm('')}
                >
                  <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="js-scroll-container">
          {error && <div className="form-error" style={{ padding: '12px', background: '#fef2f2', color: '#b91c1c', borderRadius: '8px', marginBottom: '16px' }}>{error}</div>}

          {!loading && filteredSearches.length === 0 ? (
            <div className="table-empty">
              <Search size={40} color="#cbd5e1" />
              <span>{searchTerm ? "No searches found matching criteria." : strings.jobSearch.noSearches}</span>
            </div>
          ) : (
            <div className="js-card-grid">
              {filteredSearches.map((search) => (
                <div
                  key={search.search_id}
                  className="js-card"
                  onClick={() => navigate(`/dashboard/job-search/${search.search_id}`)}
                >
                  <div className="js-card-header">
                    <h3 className="js-card-title">{search.title}</h3>
                  </div>

                  <div className="js-card-meta">
                    <div className="js-meta-item">
                      <User size={14} color="#64748b" />
                      <span>{search.searched_by}</span>
                    </div>
                    <div className="js-meta-item">
                      <Calendar size={14} color="#64748b" />
                      <span>{formatDate(search.search_date)}</span>
                    </div>
                  </div>

                  <div className="js-card-footer">
                    <span className="js-results-count">
                      {search.results?.total_results || 0} Matches
                    </span>
                    <button className="js-view-btn">
                      View Results
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <NewJobSearchModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSuccess={handleNewSearchSuccess}
      />
    </motion.div>
  );
};

export default JobSearchListPage;
